import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Grid, IconButton, TableRow, Typography, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { Props } from "./interfaces";

import {
  AbsensesMainContainer,
  ExtrahoursAmount,
  ExtrahoursMainContainer,
  ExpenseIconContainer,
  ExpenseInfoContainer,
} from "./styles";
import useAuthorization from "../../../../custom-hooks/useAuthorization/useAuthorization";
import { EmployeeRef } from "../../../../models/employees";
import CommentBubble from "../../../../reusable-components/CommentBubble/CommentBubble";
import ConcatenatedCell from "../../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";
import { getNormaleTime, intlFormatDateShort } from "../../../../utils/time-utils";
import { openTimesheetViewMode } from "../../../../views/my-t-and-a/my-ee-tna-utils";
import AddEditExpenseReport from "../../../../views/travel-and-expenses/ExpenseReport/AddEditExpenseReport/AddEditExpenseReport";
import { ExpenseReportView } from "../../../../views/travel-and-expenses/ExpenseReport/utils";
import AttachedDocuments from "../../../AttachedDocuments/AttachedDocuments";
import AbsenceButton from "../../../MyCalendar/AddEditAbsence/AbsenceButton";
import { AbsenceStatusIndicator } from "../../../MyCalendar/AddEditAbsence/styles";
import { ButtonsBlock } from "../../ButtonsActions/styles";
import { StyledTableCell } from "../styles";

const PendingApprovalsRow = ({ item, getWidth, actionsHandler, disableButtons, countersPerEmloyee }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_calendar.add_absence";

  const theme = useTheme();

  const hasAccess = useAuthorization([AUTHORIZATIONS.UserIsClient]);

  const clickNavigate = (employee: EmployeeRef | null) => {
    employee?.id && openTimesheetViewMode(employee.id);
  };

  const employeeCounters = item.tnAAbsence?.employee?.id
    ? countersPerEmloyee[item.tnAAbsence.employee.id]?.filter(
        (counter) => counter.year === item.tnAAbsence?.fromDate?.year || counter.year === item.tnAAbsence?.toDate?.year
      ) ?? []
    : [];

  return (
    <>
      {hasAccess && (
        <>
          {!!item?.tnAAbsence && (
            <TableRow>
              <StyledTableCell width={getWidth("actions")}>
                <ButtonsBlock>
                  <Button
                    onClick={() =>
                      actionsHandler(
                        item?.tnAAbsence?.id as string,
                        "acceptedAbsences",
                        item?.tnAAbsence?.employee?.id as string
                      )
                    }
                    disabled={disableButtons}
                    variant={"contained"}
                    color={"success"}
                  >
                    {t("buttons.accept")}
                  </Button>
                  <Button
                    onClick={() =>
                      actionsHandler(
                        item?.tnAAbsence?.id as string,
                        "rejectedAbsences",
                        item?.tnAAbsence?.employee?.id as string
                      )
                    }
                    disabled={disableButtons}
                    variant={"contained"}
                    color={"warning"}
                  >
                    {t("buttons.reject")}
                  </Button>
                </ButtonsBlock>
              </StyledTableCell>

              <StyledTableCell
                title={item?.tnAAbsence?.legalEntity?.name ?? ""}
                width={getWidth("employee")}
              >
                <ConcatenatedCell
                  aligned={"left"}
                  FirstElement={<Typography>{item?.tnAAbsence.employee?.name ?? ""}</Typography>}
                  SecondElement={
                    <Typography
                      title={`${t("common_words.legal_entity")} : ${item?.tnAAbsence?.legalEntity?.name} `}
                      style={{ color: "grey" }}
                      variant="subtitle2"
                    >
                      {`( ${item?.tnAAbsence?.legalEntity?.code ?? item?.tnAAbsence?.legalEntity?.name ?? ""} )`}
                    </Typography>
                  }
                />
              </StyledTableCell>

              <StyledTableCell
                width={getWidth("date")}
                align={"center"}
              >
                <Typography>
                  {intlFormatDateShort(
                    new Date(
                      item?.tnAAbsence.fromDate?.year as number,
                      (item?.tnAAbsence.fromDate?.month as number) - 1,
                      item?.tnAAbsence.fromDate?.day
                    )
                  )}{" "}
                  -{" "}
                  {intlFormatDateShort(
                    new Date(
                      item?.tnAAbsence.toDate?.year as number,
                      (item?.tnAAbsence.toDate?.month as number) - 1,
                      item?.tnAAbsence.toDate?.day
                    )
                  )}
                </Typography>
              </StyledTableCell>

              <StyledTableCell width={getWidth("absense_extrahours")}>
                <AbsensesMainContainer>
                  <IconButton
                    sx={{
                      color: theme.palette.primary.main,
                      width: "20px",
                      height: "20px",
                      borderRadius: "6px",
                      fontSize: theme.typography.body2.fontSize,
                    }}
                    onClick={() => item.tnAAbsence?.employee && clickNavigate(item.tnAAbsence.employee)}
                    title="View timesheet"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </IconButton>

                  <Grid sx={{ width: "100px", position: "relative" }}>
                    <AbsenceStatusIndicator
                      isRejected={item.tnAAbsence.isRejected}
                      isCancelled={item.tnAAbsence.isCancelled}
                      isApproved={item.tnAAbsence.isApproved}
                      key={item.tnAAbsence.id}
                    >
                      <AbsenceButton
                        absence={item.tnAAbsence}
                        forManager
                        wrapContent
                      />
                    </AbsenceStatusIndicator>

                    {item?.tnAAbsence?.comment && (
                      <Grid sx={{ position: "absolute", right: "-15px", top: "-10px" }}>
                        <CommentBubble
                          title={`${t(`${tRP}.fields.comment`)}:`}
                          comment={item.tnAAbsence.comment}
                          customButtonStyles={{
                            width: "18px",
                            height: "18px",
                            color: theme.palette.primary.main,
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </AbsensesMainContainer>
              </StyledTableCell>

              <StyledTableCell width={getWidth("info")}>
                <Grid
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {item?.tnAAbsence?.documentation && (
                    <AttachedDocuments
                      count={item?.tnAAbsence?.documentation?.filesCount ?? 0}
                      documentIDs={[item?.tnAAbsence?.documentation?.id]}
                      buttonSize={"2x"}
                      useSimpleView
                      documentIsReadOnly
                      isWidget
                    />
                  )}
                </Grid>
              </StyledTableCell>

              <StyledTableCell width={getWidth("counters")}>
                <Grid
                  sx={{
                    display: "flex",
                    width: "100%",
                    flexDirection: "column",
                    gap: "5px",
                  }}
                >
                  {employeeCounters.map((eeCounter) => {
                    return (
                      <React.Fragment key={eeCounter.id}>
                        <Grid sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography sx={{ fontWeight: "bold" }}>In {eeCounter.year}</Typography>
                          <Typography>Total vacation balance: {eeCounter.balance.toFixed(2)} days</Typography>
                          <Typography>Used/Planned: {eeCounter.usedOrPlanned.toFixed(2)} days</Typography>
                        </Grid>
                      </React.Fragment>
                    );
                  })}
                </Grid>
              </StyledTableCell>
            </TableRow>
          )}

          {!!item?.tnAExtraHours && (
            <TableRow>
              <StyledTableCell width={getWidth("actions")}>
                <ButtonsBlock>
                  <Button
                    onClick={() =>
                      actionsHandler(
                        item?.tnAExtraHours?.id as string,
                        "acceptedExtraHours",
                        item?.tnAExtraHours?.employee?.id as string
                      )
                    }
                    disabled={disableButtons}
                    variant={"contained"}
                    color={"success"}
                  >
                    {t("buttons.accept")}
                  </Button>
                  <Button
                    onClick={() =>
                      actionsHandler(
                        item?.tnAExtraHours?.id as string,
                        "rejectedExtraHours",
                        item?.tnAExtraHours?.employee?.id as string
                      )
                    }
                    disabled={disableButtons}
                    variant={"contained"}
                    color={"warning"}
                  >
                    {t("buttons.reject")}
                  </Button>
                </ButtonsBlock>
              </StyledTableCell>

              <StyledTableCell
                title={item?.tnAExtraHours?.legalEntity?.name ?? ""}
                width={getWidth("employee")}
              >
                <ConcatenatedCell
                  aligned={"left"}
                  FirstElement={<Typography>{item?.tnAExtraHours.employee?.name ?? ""}</Typography>}
                  SecondElement={
                    <Typography
                      title={`${t("common_words.legal_entity")} : ${item?.tnAExtraHours?.legalEntity?.name} `}
                      style={{ color: "grey" }}
                      variant="subtitle2"
                    >
                      {`( ${item?.tnAExtraHours?.legalEntity?.code ?? item?.tnAExtraHours?.legalEntity?.name ?? ""} )`}
                    </Typography>
                  }
                />
              </StyledTableCell>

              <StyledTableCell
                width={getWidth("date")}
                align={"center"}
              >
                <Typography>
                  {intlFormatDateShort(
                    new Date(
                      item?.tnAExtraHours.tnADate?.year as number,
                      (item?.tnAExtraHours.tnADate?.month as number) - 1,
                      item?.tnAExtraHours.tnADate?.day
                    )
                  )}
                </Typography>
              </StyledTableCell>

              <StyledTableCell width={getWidth("absense_extrahours")}>
                <ExtrahoursMainContainer>
                  <IconButton
                    sx={{
                      color: theme.palette.primary.main,
                      width: "20px",
                      height: "20px",
                      borderRadius: "6px",
                      fontSize: theme.typography.body2.fontSize,
                    }}
                    onClick={() => item?.tnAExtraHours?.employee && clickNavigate(item.tnAExtraHours.employee)}
                    title="View timesheet"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </IconButton>
                  <React.Fragment>{`${getNormaleTime(
                    item?.tnAExtraHours.fromHour,
                    item?.tnAExtraHours.fromMinute
                  )} - ${getNormaleTime(item?.tnAExtraHours.toHour, item?.tnAExtraHours.toMinute)}`}</React.Fragment>
                  <ExtrahoursAmount>{item?.tnAExtraHours.amount.toFixed(2)}</ExtrahoursAmount>
                </ExtrahoursMainContainer>
              </StyledTableCell>

              <StyledTableCell width={getWidth("info")}>
                <Typography>Extra hours</Typography>
              </StyledTableCell>

              <StyledTableCell width={getWidth("counters")}>
                <Typography></Typography>
              </StyledTableCell>
            </TableRow>
          )}

          {!!item?.expenseReport && (
            <TableRow>
              <StyledTableCell width={getWidth("actions")}>
                <ButtonsBlock>
                  <Button
                    onClick={() =>
                      actionsHandler(
                        item?.expenseReport?.id as string,
                        "acceptedExpenseReports",
                        item?.expenseReport?.employee?.id as string
                      )
                    }
                    disabled={disableButtons}
                    variant={"contained"}
                    color={"success"}
                  >
                    {t("buttons.accept")}
                  </Button>
                  <Button
                    onClick={() =>
                      actionsHandler(
                        item?.expenseReport?.id as string,
                        "rejectedExpenseReports",
                        item?.expenseReport?.employee?.id as string
                      )
                    }
                    disabled={disableButtons}
                    variant={"contained"}
                    color={"warning"}
                  >
                    {t("buttons.reject")}
                  </Button>
                </ButtonsBlock>
              </StyledTableCell>

              <StyledTableCell
                title={item?.expenseReport?.legalEntity?.name ?? ""}
                width={getWidth("employee")}
              >
                <ConcatenatedCell
                  aligned={"left"}
                  FirstElement={<Typography>{item?.expenseReport?.employee?.name ?? ""}</Typography>}
                  SecondElement={
                    <Typography
                      title={`${t("common_words.legal_entity")} : ${item?.expenseReport?.legalEntity?.name} `}
                      style={{ color: "grey" }}
                      variant="subtitle2"
                    >
                      {`( ${item?.expenseReport?.legalEntity?.code ?? item?.expenseReport?.legalEntity?.name ?? ""} )`}
                    </Typography>
                  }
                />
              </StyledTableCell>

              <StyledTableCell
                width={getWidth("date")}
                align={"center"}
              >
                <Typography>{intlFormatDateShort(new Date(item?.expenseReport.date))}</Typography>
              </StyledTableCell>

              <StyledTableCell width={getWidth("absense_extrahours")}>
                <ExpenseInfoContainer>
                  <ExpenseIconContainer>
                    <AddEditExpenseReport
                      wholeReportIsReadOnly
                      effect="edit"
                      item={item?.expenseReport}
                      view={ExpenseReportView.employee}
                      employee={item?.expenseReport?.employee}
                      buttonType="icon"
                      hideAuditNotes
                    />
                  </ExpenseIconContainer>
                  <Typography>{`${item?.expenseReport.totalAmount.toLocaleCustom()} ${
                    item?.expenseReport.currency?.code ?? ""
                  }`}</Typography>
                </ExpenseInfoContainer>
              </StyledTableCell>

              <StyledTableCell width={getWidth("info")}>
                <Typography>{item?.expenseReport?.description ?? ""}</Typography>
              </StyledTableCell>

              <StyledTableCell width={getWidth("counters")}>
                <Typography></Typography>
              </StyledTableCell>
            </TableRow>
          )}
        </>
      )}
    </>
  );
};

export default PendingApprovalsRow;
